import type React from 'react';
import { Button, Modal, Typography } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';

/** The action's tone: `danger` when it loses the work, `primary` when it keeps it. */
export type WorkConfirmTone = 'danger' | 'primary';

type WorkConfirmDialogProps = {
    isOpen: boolean;
    /** The decision as a question. */
    title: string;
    /** What happens to the work, its counts in words (`workSentenceOf`). */
    body: React.ReactNode;
    /** The safe choice, which leaves everything as it was. */
    keepLabel: string;
    /** The action, named by its verb. */
    actionLabel: string;
    tone: WorkConfirmTone;
    onKeep: () => void;
    onConfirm: () => void;
};

/**
 * Asked before a step does something to the owner's work on the file:
 * replacing the file loses it (`danger`), changing the branch keeps it
 * (`primary`). The grammar of the flow's other dialogs (the leave guard, the
 * row drawer's discard, Undo import, Done's finish): the title asks, the safe
 * choice comes first as a tertiary button, the action second, and
 * dismissing the dialog (Escape, the backdrop, its close button) is the
 * safe choice. No icon.
 *
 * Test IDs: `catalog-import-work-confirm` on the dialog,
 * `catalog-import-work-confirm-keep` and `catalog-import-work-confirm-action`
 * on its buttons.
 */
export const WorkConfirmDialog: React.FC<WorkConfirmDialogProps> = ({
    isOpen,
    title,
    body,
    keepLabel,
    actionLabel,
    tone,
    onKeep,
    onConfirm,
}) => (
    <Modal isOpen={isOpen} onOpenChange={(open) => !open && onKeep()}>
        <Modal.Backdrop>
            <Modal.Container size="md">
                <Modal.Dialog
                    className="w-full max-w-lg"
                    {...getNodeTestIdProp('catalog-import-work-confirm')}
                >
                    <Modal.CloseTrigger />
                    <Modal.Header>
                        <Modal.Heading>{title}</Modal.Heading>
                    </Modal.Header>
                    <Modal.Body>
                        <Typography color="muted" type="body-sm">
                            {body}
                        </Typography>
                    </Modal.Body>
                    {/* A phone stacks the buttons, the action on top, each
                        full width: two Georgian labels do not fit one row.
                        Keep stays first in the DOM. */}
                    <Modal.Footer className="flex-col-reverse items-stretch sm:flex-row sm:items-center">
                        <Button
                            className="w-full sm:w-auto"
                            onPress={onKeep}
                            variant="tertiary"
                            {...getNodeTestIdProp(
                                'catalog-import-work-confirm-keep',
                            )}
                        >
                            {keepLabel}
                        </Button>
                        <Button
                            className="w-full sm:w-auto"
                            onPress={onConfirm}
                            variant={tone}
                            {...getNodeTestIdProp(
                                'catalog-import-work-confirm-action',
                            )}
                        >
                            {actionLabel}
                        </Button>
                    </Modal.Footer>
                </Modal.Dialog>
            </Modal.Container>
        </Modal.Backdrop>
    </Modal>
);
